import type { PaymentFailure } from '@/shared/types/api';

export const PAYMENT_FAILURE_CAPTION_KEYS = {
  generic: 'PaymentTerminal.captionPaymentError',
  notEnoughFunds: 'PaymentTerminal.failureCaptions.notEnoughFunds',
  expiredCard: 'PaymentTerminal.failureCaptions.expiredCard',
  cardDeclined: 'PaymentTerminal.failureCaptions.cardDeclined',
  cardError: 'PaymentTerminal.failureCaptions.cardError',
  processingError: 'PaymentTerminal.failureCaptions.processingError',
  readerError: 'PaymentTerminal.failureCaptions.readerError',
  timeout: 'PaymentTerminal.failureCaptions.timeout',
  final: 'PaymentTerminal.captionPaymentFinal',
} as const;

export type PaymentFailureCaptionKey =
  (typeof PAYMENT_FAILURE_CAPTION_KEYS)[keyof typeof PAYMENT_FAILURE_CAPTION_KEYS];

type FailureFields = Pick<
  PaymentFailure,
  'category' | 'code' | 'decline_code' | 'stripe_code'
>;

const NOT_ENOUGH_FUNDS_CODES: ReadonlySet<string> = new Set([
  'insufficient_funds',
  'card_velocity_exceeded',
]);

const EXPIRED_CARD_CODES: ReadonlySet<string> = new Set(['expired_card']);

function hasCode(failure: FailureFields, codes: ReadonlySet<string>): boolean {
  return [failure.decline_code, failure.code, failure.stripe_code].some(
    (code) => typeof code === 'string' && codes.has(code)
  );
}

// The visitor sees only the reasons that the visitor can correct. A decline
// for a lost, stolen or restricted card shows the general decline text.
export function failureCaptionKey(
  failure: FailureFields | null | undefined
): PaymentFailureCaptionKey | null {
  if (!failure) {
    return null;
  }
  const isCardFailure =
    failure.category === 'card_declined' || failure.category === 'card_error';
  if (isCardFailure && hasCode(failure, NOT_ENOUGH_FUNDS_CODES)) {
    return PAYMENT_FAILURE_CAPTION_KEYS.notEnoughFunds;
  }
  if (isCardFailure && hasCode(failure, EXPIRED_CARD_CODES)) {
    return PAYMENT_FAILURE_CAPTION_KEYS.expiredCard;
  }
  switch (failure.category) {
    case 'card_declined':
      return PAYMENT_FAILURE_CAPTION_KEYS.cardDeclined;
    case 'card_error':
      return PAYMENT_FAILURE_CAPTION_KEYS.cardError;
    case 'processing_error':
      return PAYMENT_FAILURE_CAPTION_KEYS.processingError;
    case 'reader_error':
      return PAYMENT_FAILURE_CAPTION_KEYS.readerError;
    default:
      return null;
  }
}

export function paymentErrorCaptionKey({
  failure,
  timedOut,
}: {
  failure?: FailureFields | null;
  timedOut: boolean;
}): PaymentFailureCaptionKey {
  if (timedOut) {
    return PAYMENT_FAILURE_CAPTION_KEYS.timeout;
  }
  return failureCaptionKey(failure) ?? PAYMENT_FAILURE_CAPTION_KEYS.generic;
}

// After the last failure the screen has no retry. Each cause text that asks
// for one more try or another card gives way to the final text. The reader
// text already sends the visitor to a staff member, thus it stays.
export function finalPaymentCaptionKey(
  causeKey: PaymentFailureCaptionKey
): PaymentFailureCaptionKey {
  return causeKey === PAYMENT_FAILURE_CAPTION_KEYS.readerError
    ? causeKey
    : PAYMENT_FAILURE_CAPTION_KEYS.final;
}
